import React from 'react';
import { FormattedMessage, defineMessages, injectIntl } from 'react-intl';
import { Link } from 'react-router';
import get from 'lodash/get';
import PropTypes from 'prop-types';
import Header from '../../components/Header';
import Button from '../../components/Button';
import Toast from '../../components/Toast';
import Icon from '../../components/Icon';
import PageTitle from '../../components/PageTitle';
import SmartFormattedRelativeTime from '../../components/SmartFormattedRelativeTime';
import './styles.css';

export const isLastTransferFailure = (transferCreatedAt, successfulJob, lastModified) => {
  if (transferCreatedAt && !successfulJob && (parseInt(transferCreatedAt, 10) > parseInt(lastModified, 10))) {
    return true;
  }
  return false;
};

const isConfigIssue = (connectionTestSuccess, serverFingerprintEnabled, serverFingerprintMismatch,
  transferCreatedAt, successfulJob, lastModified) => {
  let result = false;
  if (!connectionTestSuccess) {
    result = true;
  } else if (connectionTestSuccess && serverFingerprintEnabled && serverFingerprintMismatch) {
    result = true;
  } else result = isLastTransferFailure(transferCreatedAt, successfulJob, lastModified);
  return result;
};

const intersectPrefix = 'settings.sftp-settings-intersect';
export const messages = defineMessages({
  header: {
    id: 'settings.sftp-settings.header',
    defaultMessage: 'SFTP Data Transfer',
  },
  pageInfo: {
    id: 'settings.sftp-settings.pageInfo',
    defaultMessage: 'Establish an automated secure file transfer using SFTP for the data listed below.',
  },
  activeMatchHeading: {
    id: 'settings.sftp-settings.activeMatchHeading',
    defaultMessage: 'Naviance Activematch',
  },
  intersectActiveMatchHeading: {
    id: `${intersectPrefix}.activeMatchHeading`,
    defaultMessage: 'Intersect Connection',
  },
  fieldGroupHeading: {
    id: 'settings.sftp-settings.fieldGroupHeading',
    defaultMessage: 'Student Connections',
  },
  setupConnectionInfo: {
    id: 'settings.sftp-settings.setupConnectionInfo',
    defaultMessage: 'Transfer new ActiveMatch student connections to a location of your choice.',
  },
  intersectSetupConnectionInfo: {
    id: `${intersectPrefix}.setupConnectionInfo`,
    defaultMessage: 'Transfer new Intersect connections to a location of your choice.',
  },
  setupConnectionButton: {
    id: 'settings.sftp-settings.setupConnectionButton',
    defaultMessage: 'Set up connection',
  },
});

const newSFTPSettingsLink = '/settings/sftp-settings/new-sftp-settings';

class SFTPSettingsPage extends React.PureComponent {
  static propTypes = {
    location: PropTypes.object,
    viewer: PropTypes.object,
  };

  state = {
    toastMessage: null,
  };

  successNotification = (
    <Toast
      active
      title=""
      kind="success"
      onDeactivate={() => this.setState({ toastMessage: null })}
      description="Configuration saved successfully"
    />
  );

  deleteNotification = (
    <Toast
      active
      title=""
      kind="success"
      onDeactivate={() => this.setState({ toastMessage: null })}
      description="Configuration deleted"
    />
  );

  componentWillMount() {
    if (get(this.props, 'location.state.success')) {
      this.setState({
        toastMessage: this.successNotification,
      });
      this.props.location.state.success = false;
    }

    if (get(this.props, 'location.state.delete')) {
      this.setState({
        toastMessage: this.deleteNotification,
      });
      this.props.location.state.delete = false;
    }
  }

  render() {
    const { toastMessage } = this.state;
    const amExportConfig = get(this.props, 'viewer.activeMatchExportConfiguration', {});
    const isNew = get(amExportConfig, 'isNew', true);
    const sftpHost = get(amExportConfig, 'sftpHost', '');
    const connectionTestSuccess = get(amExportConfig, 'connectionTestSuccess', false);
    const serverFingerprintMismatch = get(amExportConfig, 'serverFingerprintMismatch', false);
    const serverFingerprintEnabled = get(amExportConfig, 'serverFingerprintEnabled', false);
    const lastModified = get(amExportConfig, 'lastModified', null);
    const summary = get(this.props, 'viewer.activeMatchExportTransferSummary', {});
    const { successfulJob, lastSuccessAt, createdAt: transferCreatedAt } = summary;
    /* istanbul ignore next */
    let TransferMessage = () => (<span />);
    if (!transferCreatedAt) {
      TransferMessage = () => (<span>No transfers have occurred yet</span>);
    } else if (!successfulJob) {
      if (lastSuccessAt) {
        TransferMessage = () => (
          <span>
            Last transfer failed. Retrying on next scheduled day. Last success <SmartFormattedRelativeTime
              value={new Date(lastSuccessAt)}
            />
          </span>
        );
      } else {
        TransferMessage = () => (<span>No successful transfers yet. Last transfer failed. Retrying on next scheduled day</span>);
      }
    } else {
      TransferMessage = () => (<span>Last transfer <SmartFormattedRelativeTime value={new Date(transferCreatedAt)} /></span>);
    }
    return (
      <div styleName="sftp-settings-container">
        {toastMessage}
        <PageTitle subTitle="SFTP List" title="Settings" category="Intersect" />
        <Header as="h2" id="right-side-heading">
          <FormattedMessage {...messages.header} />
        </Header>
        <p><FormattedMessage {...messages.pageInfo} /></p>
        <Header as="h3" styleName="form-section-heading">
          <FormattedMessage {...messages.intersectActiveMatchHeading} />
        </Header>
        <Header as="h4">
          <FormattedMessage {...messages.fieldGroupHeading} />
        </Header>
        {
          isNew ? (
            <div styleName="setup-connection">
              <p>
                <FormattedMessage {...messages.intersectSetupConnectionInfo} />
              </p>
              <Button as={Link} to={newSFTPSettingsLink} styleName="setup-connection-btn" data-cy="sftp-data-transfer-setup-connection-button" primary>
                <FormattedMessage {...messages.setupConnectionButton} />
              </Button>
            </div>
          ): (
            <div styleName="existingConnection">
              <p styleName="connectionName">SFTP connection to {sftpHost}
                <Link to={newSFTPSettingsLink} styleName="editLink" data-cy="sftp-data-transfer-edit-button">Edit</Link>
              </p>
              <p id="transferMessage">{<TransferMessage />}
                {isConfigIssue(connectionTestSuccess, serverFingerprintEnabled, serverFingerprintMismatch,
                  transferCreatedAt, successfulJob, lastModified) ? (
                    <span>
                      <Icon name="exclamation triangle" color="orange" styleName="configurationIssues" />
                      <Link to={newSFTPSettingsLink} styleName="configurationIssuesText"> Configuration Issues </Link>
                    </span>
                  ):
                  null}
              </p>
            </div>
          )
}
      </div>
    );
  }
}

export default injectIntl(SFTPSettingsPage);
